import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';

import usePersistentState from '../use-persistent-state';
import { WebSocketContext } from '../websocket-context/websocket-provider';

export const enum OTC_TRADER_TABS {
  SURFACE_MARKING = 'SURFACE_MARKING',
  PRICER = 'PRICER',
}

const MARKET_PAIR_TOKENS = 'OTC_TRADER_MARKET_PAIR_TOKENS';

export const OTC_TRADER_TABS_KEYS = [
  { key: OTC_TRADER_TABS.SURFACE_MARKING, label: 'Multi-token Pricer' },
  { key: OTC_TRADER_TABS.PRICER, label: 'Pricer' },
];

type IOTCTraderProviderParams = {
  children: ReactNode;
};

type Tab = {
  key: string;
  label: string;
};

type IOTCTraderProvider = {
  currentTab: string;
  setCurrentTab: React.Dispatch<React.SetStateAction<string>>;
  marketPairTokens: any[];
  isMarketPairTokenLoading: boolean;
  addTab: (key: string, label: string) => void;
  tabs: Tab[];
  setTabs: React.Dispatch<React.SetStateAction<Tab[]>>;
  renameTab: (key: string, newName: string) => void;
  deleteTab: (tabKey: string) => void;
  closeTab: (tabKey: string) => void;
  duplicateTab: (tabKey: string) => void;
};

const getTokenPairName = (tab?: string) => {
  if (tab) {
    const spplit = tab.split('_');

    if (spplit.length >= 4) {
      return `${spplit[1]}_${spplit[2]}`;
    }
  }

  return undefined;
};

export const TAB_PREFIX = 'OTC-TAB_';

const getStorageDataPath = (key: string) => `WIDGETS/SURFACE_MARKINGS_${key}/widgets`;
const getStorageDataPricerPath = (key: string) => `OTC-TRADE/${key}/trades`;

export const OTCTraderContext = createContext({} as IOTCTraderProvider);

export const OTCTraderProvider = ({ children }: IOTCTraderProviderParams) => {
  const STATE_PATH = ['OTC-TRADER'];

  const [currentTab, setCurrentTab] = usePersistentState<string>(
    STATE_PATH,
    'currentTab',
    OTC_TRADER_TABS.SURFACE_MARKING,
  );
  const [tabs, setTabs] = usePersistentState<Tab[]>(STATE_PATH, 'tabs', OTC_TRADER_TABS_KEYS);

  const [marketPairTokens, setMarketPairTokens] = useState<any[]>([]);
  const [isMarketPairTokenLoading, setIsMarketPairTokenLoading] = useState(false);

  const { sendMessage, subscribeToChannel, isConnected } = useContext(WebSocketContext);

  const addTab = (key: string, label: string) => {
    setTabs((prevTabs) => {
      const tabExists = prevTabs.some((tab) => tab.key === key);

      if (!tabExists) {
        const newTabs = [...prevTabs];
        const newTab = { key, label };
        newTabs.push(newTab);

        return newTabs;
      }
      return prevTabs;
    });

    setCurrentTab(key);
  };

  const deleteTab = (key: string) => {
    const currentTabIndex = tabs.findIndex((tab) => tab.key === key);

    if (currentTabIndex > 1) {
      setCurrentTab(tabs[currentTabIndex - 1].key);
    } else {
      setCurrentTab(tabs[0].key);
    }

    setTabs((prev) => {
      return prev.filter((tab) => tab.key !== key);
    });

    localStorage.removeItem(getStorageDataPath(key));
    localStorage.removeItem(getStorageDataPricerPath(key));
  };

  const renameTab = (key: string, newName: string) => {
    setTabs((prev) => {
      return prev.map((tab) => {
        if (tab.key === key) {
          return {
            ...tab,
            label: newName,
          };
        } else {
          return tab;
        }
      });
    });
  };

  const closeTab = (tabKey: string) => {
    setTabs((prevTabs) => {
      const tabIndex = prevTabs.findIndex((tab) => tab.key === tabKey);

      const newTabs = prevTabs.filter((tab) => tab.key !== tabKey);

      if (currentTab === tabKey) {
        const newCurrentTab = tabIndex > 0 ? newTabs[tabIndex - 1].key : newTabs[0]?.key;
        setCurrentTab(newCurrentTab);
      }

      return newTabs;
    });
  };

  const convertArrayToObjectsForTokenPair = (array: string[]) => {
    const uniqueItems = Array.from(new Set(array));

    return uniqueItems
      .map((item) => ({
        name: item,
        value: item,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const duplicateTab = (newTab: string) => {
    const getFromLocal = (path: string) => {
      try {
        const res = localStorage.getItem(path);

        if (res) {
          return JSON.parse(res);
        }
      } catch {
        return null;
      }
    };

    const newTabKey = `${TAB_PREFIX}${getTokenPairName(currentTab)}_${newTab}`;

    const storagedData = getFromLocal(getStorageDataPath(currentTab));
    const storagedDataPricer = getFromLocal(getStorageDataPricerPath(currentTab));

    if (storagedData) {
      localStorage.setItem(getStorageDataPath(newTabKey), JSON.stringify(storagedData));
    }
    if (storagedDataPricer) {
      localStorage.setItem(getStorageDataPricerPath(newTabKey), JSON.stringify(storagedDataPricer));
    }

    addTab(newTabKey, newTab);
  };

  const getListTokens = useCallback(() => {
    const handleResponse = (response: any) => {
      if (!response) return;
      const { result } = response;

      const data = result?.binance_futures
        ? convertArrayToObjectsForTokenPair(result?.binance_futures)
        : [];

      setMarketPairTokens(data);
      setIsMarketPairTokenLoading(false);
      return;
    };

    const payload = {
      jsonrpc: '2.0',
      id: MARKET_PAIR_TOKENS,
      method: 'market_index_rate_sources',
      params: {},
    };

    setIsMarketPairTokenLoading(true);
    sendMessage(JSON.stringify(payload));
    subscribeToChannel(MARKET_PAIR_TOKENS, handleResponse);
  }, [sendMessage, isConnected]);

  useEffect(() => {
    const defaultTabsKeys: string[] = OTC_TRADER_TABS_KEYS.map((tab) => tab.key);
    const additionalTabs = tabs.filter((tab) => !defaultTabsKeys.includes(tab.key));
    const updatedTabs = [...OTC_TRADER_TABS_KEYS, ...additionalTabs];

    if (JSON.stringify(tabs) !== JSON.stringify(updatedTabs)) {
      setTabs(updatedTabs);
    }
  }, [tabs, setTabs]);

  useEffect(() => {
    getListTokens();
  }, [getListTokens]);

  return (
    <OTCTraderContext.Provider
      value={{
        currentTab,
        setCurrentTab,
        marketPairTokens,
        isMarketPairTokenLoading,
        tabs,
        addTab,
        renameTab,
        deleteTab,
        closeTab,
        duplicateTab,
        setTabs,
      }}
    >
      {children}
    </OTCTraderContext.Provider>
  );
};
